import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import DateInput from '@/components/DateInput'
import {
  WarehouseBrightSitesCatalogSyncActions,
  WarehouseBrightSitesCatalogSyncPanel,
  WarehouseBrightSitesCatalogSyncProvider,
} from '@/components/WarehouseBrightSitesCatalogSync'
import {
  applyCatalogFilters,
  buildCatalogFilterOptions,
  EMPTY_CATALOG_TABLE_FILTERS,
  hasActiveCatalogTableFilters,
  type CatalogAmountFilterOp,
  type CatalogTableFilters,
} from '@/components/catalogFilters'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import {
  migrateWarehouseSkuCrosswalkColumnWidths,
  WAREHOUSE_SKU_CROSSWALK_COLUMN_KEYS,
  WAREHOUSE_SKU_CROSSWALK_DEFAULT_WIDTHS,
  WAREHOUSE_SKU_CROSSWALK_PAGE_KEY,
} from '@/components/warehouseSkuCrosswalkTableConfig'
import { TABLE_AMOUNT_FILTER_OPS, hasActiveAmountFilter } from '@/components/tableFilterUtils'
import { useSortableTable } from '@/hooks/useSortableTable'
import type { SortDirection } from '@/hooks/useSortableTable'
import { ColumnResizeHandle, HoverHelp, TableToolbar, useColumnWidths } from '@/tableControls'
import { downloadCsvFile, fixedColumnStyle } from '@/utils/warehouseTableHelpers'
import { getSkuCrosswalk, putSkuCrosswalk } from '@/services/warehouse'
import type { WarehouseSkuCrosswalkItem } from '@/services/warehouseTypes'
import {
  loadFilterCache,
  saveFilterCache,
  WAREHOUSE_CATALOG_FILTER_CACHE_KEY,
} from '@/utils/tableFilterCache'
import { normalizeBulkSku } from '@/utils/normalizeBulkSku'
import { buildOrphanRows, type OrphanStockRow } from '@/components/warehouseCatalogOrphans'

type StockBySkuEntry = {
  sku: string
  product: string
  casesLeft: number
  avgCost: number | null
}

type CatalogFilterInput = {
  _clientId: string
  _isOrphan: boolean
  bk_sku: string
  wh_sku?: string | null
  display_name: string
  units_per_case?: number | null
  cases_left: number | null
  avg_cost: number | null
}

type LocalRow = WarehouseSkuCrosswalkItem & { _clientId: string }

type NewSkuForm = {
  bk_sku: string
  wh_sku: string
  display_name: string
  units_per_case: number | null
}

type NewSkuSeed = {
  bk_sku?: string
  display_name?: string
}

const EMPTY_NEW_SKU_FORM: NewSkuForm = {
  bk_sku: '',
  wh_sku: '',
  display_name: '',
  units_per_case: null,
}

const CATALOG_READONLY_CELL = 'flex h-8 w-full items-center'

function formatMoney(value: number | string | null | undefined): string {
  const amount = Number(value ?? 0)
  if (!Number.isFinite(amount)) return '—'
  return amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function formatQty(value: number | string | null | undefined): string {
  const qty = Number(value ?? 0)
  if (!Number.isFinite(qty)) return '—'
  return qty.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function escapeCsvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function stockForSku(
  bkSku: string,
  stockBySku: Map<string, StockBySkuEntry>
): StockBySkuEntry | undefined {
  const key = bkSku.trim().toUpperCase()
  if (!key) return undefined
  return stockBySku.get(key)
}

function normalizeSkuKey(value: string): string {
  return value.trim().toUpperCase()
}

let nextClientId = 0

function newClientId(): string {
  nextClientId += 1
  return `crosswalk-${nextClientId}`
}

function normalizeUnitsPerCase(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(Number(value))) return null
  return Math.round(Number(value))
}

function formatUnitsPerCaseDisplay(value: number | null | undefined): string {
  const normalized = normalizeUnitsPerCase(value)
  if (normalized == null) return ''
  return normalized.toLocaleString('en-US')
}

function parseUnitsPerCaseInput(raw: string): number | null {
  const cleaned = raw.trim().replace(/,/g, '')
  if (cleaned === '') return null
  return normalizeUnitsPerCase(Number(cleaned))
}

function CatalogSortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  width,
  onStartResize,
  style,
}: {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  columnKey: string
  width: number
  onStartResize: (key: string, event: ReactMouseEvent) => void
  style?: CSSProperties
}) {
  const isSorted = sortDirection !== null

  return (
    <th
      className={`sortable relative ${isSorted ? 'sorted' : ''} ${className}`}
      style={{
        ...style,
        ...fixedColumnStyle(width),
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 pr-2">
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronUpDownIcon className="h-3 w-3" />
          )}
        </span>
      </span>
      <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
    </th>
  )
}

function UnitsPerCaseInput({
  value,
  className,
  disabled,
  id,
  onChange,
}: {
  value: number | null
  className?: string
  disabled?: boolean
  id?: string
  onChange: (next: number | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  function commit() {
    onChange(parseUnitsPerCaseInput(draft))
    setEditing(false)
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      id={id}
      className={className}
      disabled={disabled}
      value={editing ? draft : formatUnitsPerCaseDisplay(value)}
      onFocus={() => {
        setDraft(formatUnitsPerCaseDisplay(value))
        setEditing(true)
      }}
      onChange={(e) => {
        setEditing(true)
        setDraft(e.target.value)
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur()
        }
      }}
    />
  )
}

function toLocalRow(row: WarehouseSkuCrosswalkItem): LocalRow {
  return {
    ...row,
    units_per_case: normalizeUnitsPerCase(row.units_per_case),
    _clientId: newClientId(),
  }
}

function isRowEmpty(row: WarehouseSkuCrosswalkItem): boolean {
  return !row.bk_sku.trim() && !row.display_name.trim()
}

function rowsForSave(rows: LocalRow[]): WarehouseSkuCrosswalkItem[] {
  return rows
    .filter((row) => !isRowEmpty(row))
    .map((row) => ({
      bk_sku: row.bk_sku.trim(),
      wh_sku: row.wh_sku?.trim() || null,
      bright_catalog_sku: row.bright_catalog_sku?.trim() || null,
      display_name: row.display_name.trim(),
      units_per_case: normalizeUnitsPerCase(row.units_per_case),
      notes: row.notes?.trim() || null,
      is_active: true,
    }))
}

function mergeResponseRows(
  current: LocalRow[],
  saved: WarehouseSkuCrosswalkItem[]
): LocalRow[] {
  if (saved.length === 0) return []
  const byBkSku = new Map(current.map((row) => [row.bk_sku.trim().toUpperCase(), row._clientId]))
  return saved.map((row) => {
    const existingId = byBkSku.get(row.bk_sku.trim().toUpperCase())
    return existingId ? { ...row, _clientId: existingId } : toLocalRow(row)
  })
}

function findDuplicateSkus(
  rows: LocalRow[],
  bkSku: string,
  whSku: string
): { bk?: boolean; wh?: boolean } {
  const bkKey = normalizeSkuKey(bkSku)
  const whKey = normalizeSkuKey(whSku)
  const duplicates: { bk?: boolean; wh?: boolean } = {}
  if (bkKey && rows.some((row) => normalizeSkuKey(row.bk_sku) === bkKey)) {
    duplicates.bk = true
  }
  if (whKey && rows.some((row) => normalizeSkuKey(row.wh_sku ?? '') === whKey)) {
    duplicates.wh = true
  }
  return duplicates
}

type WarehouseSkuCrosswalkCardProps = {
  onUpdated?: (rows: WarehouseSkuCrosswalkItem[]) => void
  /** Fired only after a successful PUT (not on initial load). */
  onSaved?: (rows: WarehouseSkuCrosswalkItem[]) => void
  stockBySku?: Map<string, StockBySkuEntry>
  catalogAsOf?: string
  onCatalogAsOfChange?: (value: string) => void
  catalogLoading?: boolean
  busy?: boolean
  catalogSkuFilterRequest?: { sku: string; nonce: number } | null
  brightSitesAuthRecoveryHint?: boolean
  onBrightSitesAuthError?: () => void
  onBrightSitesApplied?: () => void | Promise<void>
}

function catalogSortValue(
  row: LocalRow,
  key: string,
  stockBySku: Map<string, StockBySkuEntry>
): unknown {
  if (key === 'casesLeft') {
    return stockForSku(row.bk_sku, stockBySku)?.casesLeft ?? Number.NEGATIVE_INFINITY
  }
  if (key === 'avgCost') {
    return stockForSku(row.bk_sku, stockBySku)?.avgCost ?? Number.NEGATIVE_INFINITY
  }
  if (key === 'units_per_case') {
    return row.units_per_case ?? Number.NEGATIVE_INFINITY
  }
  return (row as Record<string, unknown>)[key]
}

export default function WarehouseSkuCrosswalkCard({
  onUpdated,
  onSaved,
  stockBySku = new Map(),
  catalogAsOf,
  onCatalogAsOfChange,
  catalogLoading = false,
  busy = false,
  catalogSkuFilterRequest,
  brightSitesAuthRecoveryHint = false,
  onBrightSitesAuthError,
  onBrightSitesApplied,
}: WarehouseSkuCrosswalkCardProps) {
  const [rows, setRows] = useState<LocalRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newSkuOpen, setNewSkuOpen] = useState(false)
  const [newSkuForm, setNewSkuForm] = useState<NewSkuForm>(EMPTY_NEW_SKU_FORM)
  const [newSkuError, setNewSkuError] = useState<string | null>(null)
  const [newSkuSubmitting, setNewSkuSubmitting] = useState(false)
  const [catalogFilters, setCatalogFilters] = useState<CatalogTableFilters>(() =>
    loadFilterCache(WAREHOUSE_CATALOG_FILTER_CACHE_KEY, EMPTY_CATALOG_TABLE_FILTERS)
  )
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())
  const onUpdatedRef = useRef(onUpdated)
  const onSavedRef = useRef(onSaved)
  onUpdatedRef.current = onUpdated
  onSavedRef.current = onSaved

  useEffect(() => {
    saveFilterCache(WAREHOUSE_CATALOG_FILTER_CACHE_KEY, catalogFilters)
  }, [catalogFilters])

  useEffect(() => {
    if (!catalogSkuFilterRequest) return
    setCatalogFilters({
      ...EMPTY_CATALOG_TABLE_FILTERS,
      bk_sku: [catalogSkuFilterRequest.sku],
    })
  }, [catalogSkuFilterRequest?.nonce, catalogSkuFilterRequest?.sku])

  const orphanRows = useMemo(
    (): OrphanStockRow[] => buildOrphanRows(rows, stockBySku),
    [rows, stockBySku]
  )

  const catalogFilterInputs = useMemo((): CatalogFilterInput[] => {
    const catalogInputs: CatalogFilterInput[] = rows.map((row) => {
      const stock = stockForSku(row.bk_sku, stockBySku)
      return {
        _clientId: row._clientId,
        _isOrphan: false,
        bk_sku: row.bk_sku,
        wh_sku: row.wh_sku,
        display_name: row.display_name,
        units_per_case: row.units_per_case,
        cases_left: stock?.casesLeft ?? null,
        avg_cost: stock?.avgCost ?? null,
      }
    })
    const orphanInputs: CatalogFilterInput[] = orphanRows.map((orphan) => ({
      _clientId: orphan._clientId,
      _isOrphan: true,
      bk_sku: orphan.sku,
      wh_sku: null,
      display_name: orphan.product,
      units_per_case: null,
      cases_left: orphan.casesLeft,
      avg_cost: orphan.avgCost,
    }))
    return [...catalogInputs, ...orphanInputs]
  }, [rows, orphanRows, stockBySku])

  const catalogFilterOptions = useMemo(
    () => buildCatalogFilterOptions(catalogFilterInputs),
    [catalogFilterInputs]
  )

  const filteredCatalogFilterInputs = useMemo(
    () => applyCatalogFilters(catalogFilterInputs, catalogFilters),
    [catalogFilterInputs, catalogFilters]
  )

  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        filteredCatalogFilterInputs.some(
          (input) => !input._isOrphan && input._clientId === row._clientId
        )
      ),
    [rows, filteredCatalogFilterInputs]
  )

  const filteredOrphanRows = useMemo(
    () =>
      orphanRows.filter((orphan) =>
        filteredCatalogFilterInputs.some(
          (input) => input._isOrphan && input._clientId === orphan._clientId
        )
      ),
    [orphanRows, filteredCatalogFilterInputs]
  )

  const hasActiveCatalogFilters = hasActiveCatalogTableFilters(catalogFilters)
  const hasActiveUnitsPerCaseFilter = hasActiveAmountFilter(
    catalogFilters.units_per_case,
    catalogFilters.units_per_case_op
  )
  const hasActiveCasesLeftFilter = hasActiveAmountFilter(
    catalogFilters.cases_left,
    catalogFilters.cases_left_op
  )
  const hasActiveAvgCostFilter = hasActiveAmountFilter(
    catalogFilters.avg_cost,
    catalogFilters.avg_cost_op
  )
  const catalogFilteredEmpty = rows.length > 0 && filteredRows.length === 0
  const displayRows = catalogFilteredEmpty ? [] : filteredRows

  const {
    sortedData: sortedRows,
    requestSort,
    getSortDirection,
  } = useSortableTable(
    displayRows,
    { key: 'bk_sku', direction: 'asc' },
    (row, key) => catalogSortValue(row, key, stockBySku)
  )

  useEffect(() => {
    migrateWarehouseSkuCrosswalkColumnWidths()
  }, [])

  const { getWidth, startResize, totalWidth } = useColumnWidths({
    pageKey: WAREHOUSE_SKU_CROSSWALK_PAGE_KEY,
    columnKeys: [...WAREHOUSE_SKU_CROSSWALK_COLUMN_KEYS],
    defaults: WAREHOUSE_SKU_CROSSWALK_DEFAULT_WIDTHS,
  })

  const tableStyle = useMemo(
    (): CSSProperties => ({
      width: totalWidth,
      minWidth: totalWidth,
      tableLayout: 'fixed',
    }),
    [totalWidth]
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => fixedColumnStyle(getWidth(columnKey)),
    [getWidth]
  )

  function handleCatalogFilterChange<K extends keyof CatalogTableFilters>(
    key: K,
    value: CatalogTableFilters[K]
  ) {
    setCatalogFilters((prev) => ({ ...prev, [key]: value }))
  }

  function clearCatalogFilters() {
    setCatalogFilters(EMPTY_CATALOG_TABLE_FILTERS)
  }

  function getFilterInputClass(value: string): string {
    const baseClass = 'input-sm'
    if (value) {
      return `${baseClass} ring-2 ring-blue-400 bg-blue-50`
    }
    return baseClass
  }

  function exportCatalogCsv() {
    const headers = [
      'BK SKU',
      'WH SKU',
      'Display name',
      'Units per case',
      'Cases in warehouse',
      'Avg cost per case',
    ]

    const catalogCsvRows = sortedRows.map((row) => {
      const stock = stockForSku(row.bk_sku, stockBySku)
      return [
        row.bk_sku.trim(),
        row.wh_sku?.trim() ?? '',
        row.display_name.trim(),
        row.units_per_case != null ? String(row.units_per_case) : '',
        stock ? String(stock.casesLeft) : '',
        stock?.avgCost != null ? String(stock.avgCost) : '',
      ]
        .map(escapeCsvCell)
        .join(',')
    })

    const orphanCsvRows = filteredOrphanRows.map((row) =>
      [
        row.sku,
        '',
        row.product,
        '',
        String(row.casesLeft),
        row.avgCost != null ? String(row.avgCost) : '',
      ]
        .map(escapeCsvCell)
        .join(',')
    )

    const asOfSuffix = catalogAsOf?.trim() ? `-as-of-${catalogAsOf.trim()}` : ''
    downloadCsvFile(`warehouse-catalog${asOfSuffix}.csv`, [
      headers.join(','),
      ...catalogCsvRows,
      ...orphanCsvRows,
    ].join('\n'))
  }

  const loadRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getSkuCrosswalk()
      setRows(response.rows.map(toLocalRow))
      onUpdatedRef.current?.(response.rows)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SKU crosswalk')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadRows()
  }, [loadRows])

  const persistRows = useCallback((nextRows: LocalRow[]): Promise<void> => {
    const saveTask = async () => {
      setError(null)
      try {
        const payload = rowsForSave(nextRows)
        const response = await putSkuCrosswalk(payload)
        const displayRows = mergeResponseRows(nextRows, response.rows)
        setRows(displayRows)
        onUpdatedRef.current?.(response.rows)
        onSavedRef.current?.(response.rows)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save SKU crosswalk'
        setError(message)
        throw err
      }
    }
    const promise = saveChainRef.current.then(saveTask)
    saveChainRef.current = promise.catch(() => undefined)
    return promise
  }, [])

  const scheduleSave = useCallback(
    (nextRows: LocalRow[]) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null
        void persistRows(nextRows)
      }, 400)
    },
    [persistRows]
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  function updateRow(clientId: string, patch: Partial<WarehouseSkuCrosswalkItem>) {
    setRows((current) => {
      const next = current.map((row) =>
        row._clientId === clientId ? { ...row, ...patch } : row
      )
      scheduleSave(next)
      return next
    })
  }

  function deleteRow(clientId: string) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setRows((current) => {
      const next = current.filter((row) => row._clientId !== clientId)
      void persistRows(next)
      return next
    })
  }

  function openNewSkuModal(seed?: NewSkuSeed) {
    setNewSkuForm({
      ...EMPTY_NEW_SKU_FORM,
      bk_sku: seed?.bk_sku ?? '',
      display_name: seed?.display_name ?? '',
    })
    setNewSkuError(null)
    setNewSkuOpen(true)
  }

  function closeNewSkuModal() {
    if (newSkuSubmitting) return
    setNewSkuOpen(false)
    setNewSkuForm(EMPTY_NEW_SKU_FORM)
    setNewSkuError(null)
  }

  async function submitNewSku() {
    setNewSkuError(null)
    const bkSku = newSkuForm.bk_sku.trim()
    const displayName = newSkuForm.display_name.trim()
    const whSku = newSkuForm.wh_sku.trim()

    if (!bkSku) {
      setNewSkuError('BK SKU is required.')
      return
    }
    if (!displayName) {
      setNewSkuError('Display name is required.')
      return
    }

    const duplicates = findDuplicateSkus(rows, bkSku, whSku)
    if (duplicates.bk) {
      setNewSkuError('A catalog row with this BK SKU already exists.')
      return
    }
    if (duplicates.wh) {
      setNewSkuError('A catalog row with this WH SKU already exists.')
      return
    }

    const unitsPerCase = newSkuForm.units_per_case

    const newRow: LocalRow = {
      _clientId: newClientId(),
      bk_sku: bkSku,
      wh_sku: whSku || null,
      bright_catalog_sku: null,
      display_name: displayName,
      units_per_case: unitsPerCase,
      notes: null,
      is_active: true,
    }

    const previousRows = rows
    const nextRows = [...previousRows, newRow]
    setRows(nextRows)
    setNewSkuSubmitting(true)
    try {
      await persistRows(nextRows)
      setNewSkuOpen(false)
      setNewSkuForm(EMPTY_NEW_SKU_FORM)
      setNewSkuError(null)
    } catch (err) {
      setRows(previousRows)
      setNewSkuError(err instanceof Error ? err.message : 'Failed to save new SKU')
    } finally {
      setNewSkuSubmitting(false)
    }
  }

  return (
    <WarehouseBrightSitesCatalogSyncProvider
      busy={busy}
      authRecoveryHint={brightSitesAuthRecoveryHint}
      onAuthError={onBrightSitesAuthError}
      onApplied={async () => {
        await loadRows()
        await onBrightSitesApplied?.()
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="container-title">Product catalog</h2>
            <p className="mt-1 text-xs text-gray-500">
              BK SKU, warehouse pull ID, display name, units per case, and current warehouse stock
              from the ledger.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <WarehouseBrightSitesCatalogSyncActions />
            <button
              type="button"
              className="btn btn-primary text-sm"
              disabled={loading || newSkuSubmitting}
              onClick={() => openNewSkuModal()}
            >
              New SKU
            </button>
          </div>
        </div>

        <WarehouseBrightSitesCatalogSyncPanel />

      {loading ? (
        <div className="mt-4 flex shrink-0 items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Loading catalog…
        </div>
      ) : (
        <>
          <div className="mt-4 shrink-0 space-y-3 rounded-lg border border-gray-100 bg-gray-50/80 p-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Filters
            </span>
            <div className="filter-bar filter-bar--catalog !border-0 !bg-transparent !p-0">
            <div className="flex flex-col gap-1">
              <div className="text-xs font-medium text-gray-600">
                <HoverHelp label="As-of date" aria-label="Catalog as-of date help">
                  Stock totals sum signed cases from the ledger through this date (voided rows
                  excluded).
                </HoverHelp>
                {catalogLoading ? (
                  <span className="ml-1 font-normal text-gray-400">Loading…</span>
                ) : null}
              </div>
              <DateInput
                className="input h-9 text-sm"
                value={catalogAsOf ?? ''}
                dateFormat="iso"
                disabled={catalogLoading || busy || !onCatalogAsOfChange}
                onChange={(value) => onCatalogAsOfChange?.(value)}
              />
            </div>
            <MultiSelectFilter
              id="catalog-filter-bk-sku"
              label="BK SKU"
              options={catalogFilterOptions.bk_sku}
              selected={catalogFilters.bk_sku}
              onChange={(value) => handleCatalogFilterChange('bk_sku', value)}
              searchable
            />
            <MultiSelectFilter
              id="catalog-filter-wh-sku"
              label="WH SKU"
              options={catalogFilterOptions.wh_sku}
              selected={catalogFilters.wh_sku}
              onChange={(value) => handleCatalogFilterChange('wh_sku', value)}
              searchable
            />
            <MultiSelectFilter
              id="catalog-filter-display-name"
              label="Display name"
              options={catalogFilterOptions.display_name}
              selected={catalogFilters.display_name}
              onChange={(value) => handleCatalogFilterChange('display_name', value)}
              searchable
            />
            <div className="filter-bar-amount flex flex-col gap-1">
              <label
                htmlFor="catalog-filter-units-per-case-value"
                className={`text-xs font-medium ${hasActiveUnitsPerCaseFilter ? 'text-blue-600' : 'text-gray-600'}`}
              >
                Units per case
              </label>
              <div className="filter-bar-amount-controls">
                <select
                  id="catalog-filter-units-per-case-op"
                  aria-label="Units per case comparison"
                  className={
                    hasActiveUnitsPerCaseFilter
                      ? 'filter-bar-amount-op select ring-2 ring-blue-400 bg-blue-50'
                      : 'filter-bar-amount-op select'
                  }
                  value={catalogFilters.units_per_case_op}
                  onChange={(e) =>
                    handleCatalogFilterChange(
                      'units_per_case_op',
                      e.target.value as CatalogAmountFilterOp
                    )
                  }
                >
                  {TABLE_AMOUNT_FILTER_OPS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  id="catalog-filter-units-per-case-value"
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  className={`filter-bar-amount-value ${getFilterInputClass(catalogFilters.units_per_case)}`}
                  value={catalogFilters.units_per_case}
                  onChange={(e) =>
                    handleCatalogFilterChange('units_per_case', e.target.value)
                  }
                />
              </div>
            </div>
            <div className="filter-bar-amount flex flex-col gap-1">
              <label
                htmlFor="catalog-filter-cases-left-value"
                className={`text-xs font-medium ${hasActiveCasesLeftFilter ? 'text-blue-600' : 'text-gray-600'}`}
              >
                Cases in warehouse
              </label>
              <div className="filter-bar-amount-controls">
                <select
                  id="catalog-filter-cases-left-op"
                  aria-label="Cases in warehouse comparison"
                  className={
                    hasActiveCasesLeftFilter
                      ? 'filter-bar-amount-op select ring-2 ring-blue-400 bg-blue-50'
                      : 'filter-bar-amount-op select'
                  }
                  value={catalogFilters.cases_left_op}
                  onChange={(e) =>
                    handleCatalogFilterChange(
                      'cases_left_op',
                      e.target.value as CatalogAmountFilterOp
                    )
                  }
                >
                  {TABLE_AMOUNT_FILTER_OPS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  id="catalog-filter-cases-left-value"
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  className={`filter-bar-amount-value ${getFilterInputClass(catalogFilters.cases_left)}`}
                  value={catalogFilters.cases_left}
                  onChange={(e) => handleCatalogFilterChange('cases_left', e.target.value)}
                />
              </div>
            </div>
            <div className="filter-bar-amount flex flex-col gap-1">
              <label
                htmlFor="catalog-filter-avg-cost-value"
                className={`text-xs font-medium ${hasActiveAvgCostFilter ? 'text-blue-600' : 'text-gray-600'}`}
              >
                Avg cost per case
              </label>
              <div className="filter-bar-amount-controls">
                <select
                  id="catalog-filter-avg-cost-op"
                  aria-label="Avg cost per case comparison"
                  className={
                    hasActiveAvgCostFilter
                      ? 'filter-bar-amount-op select ring-2 ring-blue-400 bg-blue-50'
                      : 'filter-bar-amount-op select'
                  }
                  value={catalogFilters.avg_cost_op}
                  onChange={(e) =>
                    handleCatalogFilterChange(
                      'avg_cost_op',
                      e.target.value as CatalogAmountFilterOp
                    )
                  }
                >
                  {TABLE_AMOUNT_FILTER_OPS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  id="catalog-filter-avg-cost-value"
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  className={`filter-bar-amount-value ${getFilterInputClass(catalogFilters.avg_cost)}`}
                  value={catalogFilters.avg_cost}
                  onChange={(e) => handleCatalogFilterChange('avg_cost', e.target.value)}
                />
              </div>
            </div>
            {hasActiveCatalogFilters ? (
              <button
                type="button"
                className="btn btn-secondary self-end px-3 py-1.5 text-sm"
                onClick={clearCatalogFilters}
              >
                Clear
              </button>
            ) : null}
            </div>
          </div>
          {catalogFilterInputs.length > 0 ? (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="min-w-0 truncate text-xs sm:text-sm text-gray-500">
                {hasActiveCatalogFilters ? (
                  <>
                    Showing {filteredCatalogFilterInputs.length} of {catalogFilterInputs.length}{' '}
                    (filtered)
                  </>
                ) : (
                  <>
                    Showing {filteredCatalogFilterInputs.length} SKU
                    {filteredCatalogFilterInputs.length !== 1 ? 's' : ''}
                  </>
                )}
              </p>
              <TableToolbar onExport={exportCatalogCsv} exportDisabled={loading} />
            </div>
          ) : null}
          {catalogFilterInputs.length > 0 && filteredCatalogFilterInputs.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">No rows match the current filters.</p>
          ) : rows.length === 0 && filteredOrphanRows.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">
              No catalog SKUs yet. Click New SKU to add one.
            </p>
          ) : (
          <div className="table-scroll-mobile mt-4 min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-lg border border-gray-200">
            <table
              className="table-forecast border-separate border-spacing-0"
              style={tableStyle}
            >
              <thead>
                <tr>
                  <CatalogSortableHeader
                    label={
                      <HoverHelp label="BK SKU" aria-label="BK SKU help">
                        Canonical inventory / FIFO key used for warehouse layers and movements.
                      </HoverHelp>
                    }
                    sortKey="bk_sku"
                    sortDirection={getSortDirection('bk_sku')}
                    onSort={requestSort}
                    columnKey="bk_sku"
                    width={getWidth('bk_sku')}
                    onStartResize={startResize}
                  />
                  <CatalogSortableHeader
                    label={
                      <HoverHelp label="WH SKU" aria-label="WH SKU help">
                        Halo warehouse pull ID as it appears on TXCGB billing files.
                      </HoverHelp>
                    }
                    sortKey="wh_sku"
                    sortDirection={getSortDirection('wh_sku')}
                    onSort={requestSort}
                    columnKey="wh_sku"
                    width={getWidth('wh_sku')}
                    onStartResize={startResize}
                  />
                  <CatalogSortableHeader
                    label={
                      <HoverHelp label="Display name" aria-label="Display name help">
                        Label shown throughout the Warehouse page for this product.
                      </HoverHelp>
                    }
                    sortKey="display_name"
                    sortDirection={getSortDirection('display_name')}
                    onSort={requestSort}
                    columnKey="display_name"
                    width={getWidth('display_name')}
                    onStartResize={startResize}
                  />
                  <CatalogSortableHeader
                    label={
                      <HoverHelp label="Units per case" aria-label="Units per case help">
                        Optional packaging factor for this SKU (read-only reference on manual entries).
                      </HoverHelp>
                    }
                    sortKey="units_per_case"
                    sortDirection={getSortDirection('units_per_case')}
                    onSort={requestSort}
                    columnKey="units_per_case"
                    width={getWidth('units_per_case')}
                    onStartResize={startResize}
                  />
                  <CatalogSortableHeader
                    label={
                      <HoverHelp
                        label="Cases in Warehouse"
                        aria-label="Cases in Warehouse help"
                      >
                        Net cases on hand, summed from the ledger (receipts and adjustments minus
                        store shipments).
                      </HoverHelp>
                    }
                    sortKey="casesLeft"
                    sortDirection={getSortDirection('casesLeft')}
                    onSort={requestSort}
                    columnKey="casesLeft"
                    width={getWidth('casesLeft')}
                    onStartResize={startResize}
                  />
                  <CatalogSortableHeader
                    label={
                      <HoverHelp label="Avg cost per case" aria-label="Avg cost per case help">
                        Case-weighted average unit cost from manufacturer purchases and store
                        transfers on or before the catalog as-of date.
                      </HoverHelp>
                    }
                    sortKey="avgCost"
                    sortDirection={getSortDirection('avgCost')}
                    onSort={requestSort}
                    columnKey="avgCost"
                    width={getWidth('avgCost')}
                    onStartResize={startResize}
                  />
                  <th
                    className="relative"
                    style={{
                      ...fixedColumnStyle(getWidth('actions')),
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <span className="sr-only">Actions</span>
                    <ColumnResizeHandle columnKey="actions" onStartResize={startResize} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const stock = stockForSku(row.bk_sku, stockBySku)
                  return (
                  <tr
                    key={row._clientId}
                    className="[&>td]:align-middle [&>td]:!px-1.5 [&>td]:!py-1"
                  >
                    <td style={cellStyle('bk_sku')}>
                      <input
                        className="input h-8 w-full min-w-0 text-sm"
                        value={row.bk_sku}
                        onChange={(e) => updateRow(row._clientId, { bk_sku: e.target.value })}
                      />
                    </td>
                    <td style={cellStyle('wh_sku')}>
                      <input
                        className="input h-8 w-full min-w-0 text-sm"
                        value={row.wh_sku ?? ''}
                        onChange={(e) => updateRow(row._clientId, { wh_sku: e.target.value })}
                      />
                    </td>
                    <td style={cellStyle('display_name')}>
                      <input
                        className="input h-8 w-full min-w-0 text-sm"
                        value={row.display_name}
                        onChange={(e) => updateRow(row._clientId, { display_name: e.target.value })}
                      />
                    </td>
                    <td style={cellStyle('units_per_case')}>
                      <UnitsPerCaseInput
                        className="input h-8 w-full min-w-0 text-sm tabular-nums"
                        value={row.units_per_case ?? null}
                        onChange={(units_per_case) => updateRow(row._clientId, { units_per_case })}
                      />
                    </td>
                    <td className="tabular-nums text-sm text-gray-700" style={cellStyle('casesLeft')}>
                      <div className={CATALOG_READONLY_CELL}>
                        {stock ? formatQty(stock.casesLeft) : '—'}
                      </div>
                    </td>
                    <td className="tabular-nums text-sm text-gray-700" style={cellStyle('avgCost')}>
                      <div className={CATALOG_READONLY_CELL}>
                        {stock
                          ? stock.avgCost != null
                            ? formatMoney(stock.avgCost)
                            : '—'
                          : '—'}
                      </div>
                    </td>
                    <td style={cellStyle('actions')}>
                      <button
                        type="button"
                        className="btn btn-secondary p-1"
                        aria-label="Remove row"
                        onClick={() => deleteRow(row._clientId)}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                  )
                })}
                {filteredOrphanRows.map((row) => (
                  <tr
                    key={row._clientId}
                    className="bg-gray-50/80 text-gray-600 [&>td]:align-middle [&>td]:!px-1.5 [&>td]:!py-1"
                  >
                    <td className="font-medium text-gray-700" style={cellStyle('bk_sku')}>
                      <div className={CATALOG_READONLY_CELL}>{row.sku}</div>
                    </td>
                    <td style={cellStyle('wh_sku')} />
                    <td style={cellStyle('display_name')}>
                      <div className={CATALOG_READONLY_CELL}>
                        <span className="italic text-gray-500" title="In warehouse stock but not in catalog">
                          {row.product}
                        </span>
                      </div>
                    </td>
                    <td style={cellStyle('units_per_case')} />
                    <td className="tabular-nums text-sm" style={cellStyle('casesLeft')}>
                      <div className={CATALOG_READONLY_CELL}>{formatQty(row.casesLeft)}</div>
                    </td>
                    <td className="tabular-nums text-sm" style={cellStyle('avgCost')}>
                      <div className={CATALOG_READONLY_CELL}>
                        {row.avgCost != null ? formatMoney(row.avgCost) : '—'}
                      </div>
                    </td>
                    <td style={cellStyle('actions')}>
                      <button
                        type="button"
                        className="btn btn-secondary p-1"
                        aria-label="Add to catalog"
                        title="Add to catalog"
                        disabled={newSkuSubmitting}
                        onClick={() =>
                          openNewSkuModal({
                            bk_sku: normalizeBulkSku(row.sku),
                            display_name: row.product ?? '',
                          })
                        }
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          {error ? (
            <div className="mt-3 shrink-0">
              <p className="text-xs text-red-600">{error}</p>
            </div>
          ) : null}
        </>
      )}

      {newSkuOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={closeNewSkuModal}
        >
          <div
            className="relative w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-gray-900">New SKU</h3>
            <p className="mt-1 text-xs text-gray-500">
              Add a catalog row with BK SKU and display name. WH SKU and units per case are optional.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label htmlFor="new-sku-bk-sku" className="text-xs font-medium text-gray-700">
                  BK SKU
                </label>
                <input
                  id="new-sku-bk-sku"
                  type="text"
                  className="input mt-1 w-full text-sm"
                  value={newSkuForm.bk_sku}
                  disabled={newSkuSubmitting}
                  onChange={(e) =>
                    setNewSkuForm((prev) => ({ ...prev, bk_sku: e.target.value }))
                  }
                />
              </div>
              <div>
                <label htmlFor="new-sku-wh-sku" className="text-xs font-medium text-gray-700">
                  WH SKU
                </label>
                <input
                  id="new-sku-wh-sku"
                  type="text"
                  className="input mt-1 w-full text-sm"
                  value={newSkuForm.wh_sku}
                  disabled={newSkuSubmitting}
                  onChange={(e) =>
                    setNewSkuForm((prev) => ({ ...prev, wh_sku: e.target.value }))
                  }
                />
              </div>
              <div>
                <label htmlFor="new-sku-display-name" className="text-xs font-medium text-gray-700">
                  Display name
                </label>
                <input
                  id="new-sku-display-name"
                  type="text"
                  className="input mt-1 w-full text-sm"
                  value={newSkuForm.display_name}
                  disabled={newSkuSubmitting}
                  onChange={(e) =>
                    setNewSkuForm((prev) => ({ ...prev, display_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="new-sku-units-per-case"
                  className="text-xs font-medium text-gray-700"
                >
                  Units per case
                </label>
                <UnitsPerCaseInput
                  id="new-sku-units-per-case"
                  className="input mt-1 w-full text-sm tabular-nums"
                  value={newSkuForm.units_per_case}
                  disabled={newSkuSubmitting}
                  onChange={(units_per_case) =>
                    setNewSkuForm((prev) => ({ ...prev, units_per_case }))
                  }
                />
              </div>
            </div>
            {newSkuError ? <p className="mt-3 text-xs text-red-600">{newSkuError}</p> : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary text-sm"
                disabled={newSkuSubmitting}
                onClick={closeNewSkuModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary text-sm"
                disabled={newSkuSubmitting}
                onClick={() => void submitNewSku()}
              >
                {newSkuSubmitting ? 'Saving…' : 'Add SKU'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </WarehouseBrightSitesCatalogSyncProvider>
  )
}
